import LoadingSpinner from 'components/LoadingSpinner';
import logger from 'logger';
import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setConferenceAction } from 'store/conference/conferenceActions';
import { deleteConferenceFile, getFileData, RemoveVideoFromConference } from 'utils/apiCalls';

const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
function bytesToSize(bytes) {
  if (bytes === 0) return '0 Byte';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
}

const FilesTable = ({ videos, sessionId }) => {
  const dispatch = useDispatch();
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);

    const getFiles = async () => {
      if (videos) {
        const promises = videos.map(async (video) => {
          if (video.Bucket && video.Bucket) return await getFileData(video.Key, video.Bucket);
        });
        const finished = await Promise.all(promises);
        const newFiles = finished.map((file) => {
          const { data, error } = file;
          if (error) return null;
          const arr = data.key.split('/');
          const name = arr[arr.length - 1];
          return {
            name,
            bucket: data.bucket,
            key: data.key,
            size: data.ContentLength,
            type: data.ContentType,
            lastModified: data.LastModified,
          };
        });
        setFileList(newFiles);
      }

      setLoading(false);
    };

    getFiles();
    return () => setLoading(false);
  }, [videos]);

  const handleDeleteFile = async (video, sessionId) => {
    logger.info('Deleting conf file from s3', { video });
    await deleteConferenceFile(video.key, video.bucket);
    const updatedSession = await RemoveVideoFromConference(sessionId, video);
    dispatch(setConferenceAction(updatedSession.data));
  };

  if (loading) return <LoadingSpinner />;

  const columns = [
    {
      name: 'name',
      label: 'Name',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'size',
      label: 'Size',
      options: {
        filter: false,
        sort: true,
        searchable: false,
        customBodyRender: (value) => bytesToSize(value),
      },
    },
    {
      name: 'lastModified',
      label: 'Last Modified',
      options: {
        filter: false,
        sort: true,
        searchable: false,
        customBodyRender: (value) => moment.utc(value).local().format('MMM D, YYYY h:mm:ss a'),
      },
    },
    {
      name: 'key',
      options: {
        filter: false,
        sort: false,
        display: false,
        viewColumns: false,
        searchable: false,
      },
    },
    {
      name: 'bucket',
      options: {
        filter: false,
        sort: false,
        display: false,
        viewColumns: false,
        searchable: false,
      },
    },
  ];

  async function onRowsDelete(rowsDeleted) {
    for (const data of rowsDeleted.data) {
      const file = fileList[data.dataIndex];
      await handleDeleteFile(file, sessionId);
    }
  }

  const options = {
    onRowsDelete,
    print: false,
    download: false,
    sortOrder: { name: 'name', direction: 'asc' },
  };

  return (
    <MUIDataTable
      style={{ width: '100%' }}
      title="Files"
      data={fileList}
      columns={columns}
      options={options}
    />
  );
};

FilesTable.propTypes = {
  videos: PropTypes.array,
  sessionId: PropTypes.string.isRequired,
};

export default FilesTable;
